import React, { forwardRef, useContext } from 'react';
import styled from 'styled-components';
import { addMinutes } from 'date-fns';
import { Text, message } from 'components';
import { Timezone } from '@bb/domain/dist/types';
import TourEditContext from '../TourEditContext';
import TourListingContext from '../TourListingContext';
import { formatToZonedTime } from '../utils';

interface TimeSelectorItemProps {
  time: Date;
  listingTimezone: Timezone;
  selectedDuration: number;
  startTime?: Date;
  timePassed: boolean;
  isSelected: boolean;
  isStartTime: boolean;
  itemId: string;
  unavailable: boolean;
  isTimeOpenHouse: {
    isOpenHouse: boolean;
    isOpenHouseStart: boolean;
    isOpenHouseEnd: boolean;
  };
  fromModal?: boolean;
  isTimeValid: (
    startTime: Date,
    duration: number,
    listingTimezone: Timezone,
  ) => { isValid: boolean; passedTourDate?: boolean };
}

const TimeSelectorItem = forwardRef<any, TimeSelectorItemProps>(
  (
    {
      time,
      listingTimezone,
      selectedDuration,
      timePassed,
      isSelected,
      isStartTime,
      itemId,
      unavailable,
      isTimeOpenHouse,
      isTimeValid,
      fromModal,
    },
    ref,
  ) => {
    const { setTourItemTime } = useContext(TourEditContext);
    // For Reschedule select times
    const { setSelectedTimes } = useContext(TourListingContext);
    const formattedTime = formatToZonedTime(time, 'h:mm a', listingTimezone);

    return (
      <ItemContainer
        ref={ref}
        $isOpenHouse={isTimeOpenHouse.isOpenHouse}
        $selected={isSelected}
        $timePassed={timePassed}
        $unavailable={unavailable}
        onClick={() => {
          const { isValid, passedTourDate } = isTimeValid(
            time,
            selectedDuration,
            listingTimezone,
          );
          // time should not be passed AND the times in the
          // selected time window should be available
          if (!timePassed && !unavailable && isValid) {
            if (fromModal) {
              setSelectedTimes([time, addMinutes(time, selectedDuration)]);
            } else {
              setTourItemTime(time, addMinutes(time, selectedDuration), itemId);
            }
          }
          if (passedTourDate) {
            message.warn('Time cannot be selected past tour date.');
          }
        }}
      >
        {isTimeOpenHouse.isOpenHouseEnd && !isSelected && (
          <StyledOpenHouseText>Open House</StyledOpenHouseText>
        )}
        <StyledTimeText
          color={isSelected && (!isStartTime ? '#62cb31' : 'white')}
        >
          {isStartTime
            ? `${formattedTime} - ${formatToZonedTime(
                addMinutes(time, selectedDuration),
                'h:mm a',
                listingTimezone,
              )}`
            : formattedTime}
        </StyledTimeText>
      </ItemContainer>
    );
  },
);

export default TimeSelectorItem;

const ItemContainer = styled.div<{
  $timePassed?: boolean;
  $unavailable?: boolean;
  $selected?: boolean;
  $isOpenHouse?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: inset 0px -1px 0px #f0f0f0;
  padding: 1rem 0;
  cursor: pointer;
  position: relative;
  ${({ $isOpenHouse }) =>
    $isOpenHouse &&
    `
    background: #6eabfe;
    border-color: #6eabfe;
    .ant-typography {
      color: #fff ;
    }
    `}
  ${({ $timePassed }) =>
    $timePassed && `opacity: .5; background: #fea186; cursor: default;`}
  ${({ $unavailable }) =>
    $unavailable && `background: #fea186; cursor: default;`} 
  ${({ $selected }) =>
    $selected &&
    `
    background: #62cb31;
    color: #62cb31!important;
    border-color: #62cb31;
    box-shadow: none;
  `}
`;

const StyledTimeText = styled(Text)`
  font-size: 14px;
`;

const StyledOpenHouseText = styled(Text)`
  left: 0;
  position: absolute;
  margin-left: 0.5rem;
  font-size: 10px;
  justify-self: start;
`;
